import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authApi } from '@/api/auth'
import { apiErrorMessage } from '@/lib/errors'
import { useAiUsageLogs } from '@/composables/useAiUsageLogs'
import { useClaudeUsage } from '@/composables/useClaudeUsage'
import { useCodeGraph } from '@/composables/useCodeGraph'
import { useDashboard } from '@/composables/useDashboard'
import { useGithubInstallations } from '@/composables/useGithubInstallations'
import { useGithubRepositorySettings } from '@/composables/useGithubRepositorySettings'
import { useInvites } from '@/composables/useInvites'
import { useLinearTaskPullRequests } from '@/composables/useLinearTaskPullRequests'
import { useLinearTeams } from '@/composables/useLinearTeams'
import { useBuildPullRequests } from '@/composables/useBuildPullRequests'
import { usePendingRepoRules } from '@/composables/usePendingRepoRules'
import { useQueueMonitor } from '@/composables/useQueueMonitor'
import { useRepoLessons } from '@/composables/useRepoLessons'
import { useRepoRules } from '@/composables/useRepoRules'
import { useRepoScans } from '@/composables/useRepoScans'
import { useReviewRuns } from '@/composables/useReviewRuns'
import { useRuleFindings } from '@/composables/useRuleFindings'
import { useRuleFindingTrends } from '@/composables/useRuleFindingTrends'
import type { LoginPayload, User } from '@/types/models'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  // Tracks whether the initial GET /auth/user probe on app load has resolved.
  const initialized = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)

  async function login(payload: LoginPayload): Promise<void> {
    loading.value = true
    try {
      user.value = await authApi.login(payload)
      initialized.value = true
    } finally {
      loading.value = false
    }
  }

  // App-load probe. Swallows a 401 (not logged in) but rethrows real errors.
  async function fetchUser(): Promise<void> {
    loading.value = true
    try {
      user.value = await authApi.user()
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status
      if (status === 401) {
        reset()
      } else {
        throw e
      }
    } finally {
      initialized.value = true
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    error.value = null
    try {
      await authApi.logout()
    } catch (e: unknown) {
      error.value = apiErrorMessage(e, 'Failed to log out.')
      throw e
    } finally {
      reset()
    }
  }

  // Clears every module-level singleton composable's cached data too — none
  // of them key their state to the logged-in user, so without this a second
  // account signing in on the same tab (shared machine, or a session that
  // expired and was resumed as someone else) would see the previous user's
  // GitHub installations, review runs, invites, etc. either flash on screen
  // or, worse, never refetch at all where a composable short-circuits on an
  // `initialized` flag.
  function reset(): void {
    user.value = null
    useGithubInstallations().reset()
    useReviewRuns().reset()
    useRepoScans().reset()
    useRepoRules().reset()
    useRepoLessons().reset()
    useCodeGraph().reset()
    useGithubRepositorySettings().reset()
    usePendingRepoRules().reset()
    useInvites().reset()
    useLinearTeams().reset()
    useLinearTaskPullRequests().reset()
    useBuildPullRequests().reset()
    useAiUsageLogs().reset()
    useClaudeUsage().reset()
    useQueueMonitor().reset()
    useDashboard().reset()
    useRuleFindingTrends().reset()
    useRuleFindings().reset()
  }

  function clearError(): void {
    error.value = null
  }

  // Also used to establish a brand-new session (e.g. right after accepting an
  // invite), not just to refresh an already-initialized one — so it must set
  // `initialized` too, or the router guard's fetchUser() probe fires again on
  // the very next navigation.
  function setUser(updated: User): void {
    user.value = updated
    initialized.value = true
  }

  return { user, initialized, loading, error, isAuthenticated, login, fetchUser, logout, reset, setUser, clearError }
})
